/**
 * Pure touch/viewport geometry. No DOM, no imports, no side effects.
 *
 * Everything here was previously an inline literal duplicated across files that
 * could not agree with each other. Extracting it serves two purposes: one
 * definition instead of several, and — because these are the decisions that
 * actually determine whether a gesture feels right — behaviour that can be unit
 * tested without a browser. `mobile-shell-smoke.mjs` should catch rendering,
 * not be the first proof of the arithmetic.
 *
 * Boundary semantics are load-bearing and deliberately NOT normalised: scroll
 * release is strictly greater-than, activation is greater-than-or-equal. Those
 * are the comparisons the shipped code uses, and quietly "tidying" them changes
 * how a drag feels at exactly the threshold.
 */

// ── drag thresholds ───────────────────────────────────────────────────────

/** Pre-arm finger travel that releases a candidate so the page scrolls natively. */
export const SCROLL_RELEASE_PX = 10;
/** Post-arm travel before a drag reports movement. */
export const ACTIVATION_PX = 5;
/** Touch/pen long-press before a drag arms. */
export const TOUCH_LONG_PRESS_MS = 250;

/**
 * Has the pointer moved far enough BEFORE arming that we should let go and let
 * the page scroll? Strictly greater-than: `reorder-preview.js` uses
 * `> TOUCH_SCROLL_RELEASE_PX2` (100 === 10²) and `section-list.js` uses
 * `> 100`. At exactly 10px the candidate is retained.
 */
export function exceedsScrollRelease(dx, dy, thresholdPx = SCROLL_RELEASE_PX) {
  return dx * dx + dy * dy > thresholdPx * thresholdPx;
}

/**
 * Has the pointer moved far enough AFTER arming to count as a drag?
 * Greater-than-or-equal: `reorder-preview.js` early-returns on
 * `dx * dx + dy * dy < 25`, so exactly 5px DOES activate.
 */
export function exceedsActivation(dx, dy, thresholdPx = ACTIVATION_PX) {
  return dx * dx + dy * dy >= thresholdPx * thresholdPx;
}

// ── soft keyboard ─────────────────────────────────────────────────────────

/**
 * Height the soft keyboard occupies, from `visualViewport` geometry.
 *
 * The floor exists because mobile Safari collapsing its URL bar also shrinks
 * the visual viewport — by roughly 40-60px — and that must not read as a
 * keyboard. Anything under the floor is reported as zero rather than as a small
 * inset, because a small wrong inset is worse than none: it shifts layout for
 * no reason.
 *
 * @param {number} layoutHeight  window.innerHeight
 * @param {number} visualHeight  visualViewport.height
 * @param {number} visualOffsetTop visualViewport.offsetTop
 * @param {number} [minPx=60]    floor below which the result is 0
 * @returns {number} px, never negative
 */
export function computeKeyboardInset(layoutHeight, visualHeight, visualOffsetTop, minPx = 60) {
  if (!Number.isFinite(layoutHeight) || !Number.isFinite(visualHeight)) return 0;
  const offset = Number.isFinite(visualOffsetTop) ? visualOffsetTop : 0;
  const raw = layoutHeight - visualHeight - offset;
  if (!Number.isFinite(raw) || raw < minPx) return 0;
  return raw;
}

// ── canvas fit-to-width ───────────────────────────────────────────────────

/**
 * Scale that fits a device-preset canvas into the space actually available.
 *
 * Returns exactly 1 whenever the preset already fits — it never scales UP.
 * That property is what keeps desktop behaviour byte-identical: on any desktop
 * width the preset fits, so the derived scale is 1 and the value sent to the
 * iframe is the user's zoom unchanged.
 *
 * @returns {number} in [min, max], or exactly 1 when no scaling is needed
 */
export function computeFitScale(availableWidth, presetWidth, min = 0.25, max = 1.5) {
  if (!Number.isFinite(availableWidth) || !Number.isFinite(presetWidth)) return 1;
  if (availableWidth <= 0 || presetWidth <= 0) return 1;
  if (presetWidth <= availableWidth) return 1;
  return Math.min(max, Math.max(min, availableWidth / presetWidth));
}

// ── pointer kind ──────────────────────────────────────────────────────────

/**
 * Resolve the pointer register from a live event, falling back to a media query.
 *
 * Per-EVENT, never per-device. A hybrid machine (Surface, iPad + trackpad)
 * reports `hover: hover` and `pointer: fine` while its touchscreen is in use,
 * so a media query alone gives that user the desktop register under a finger.
 * The live `pointerType` wins whenever there is one.
 *
 * @param {string|undefined} pointerType   PointerEvent.pointerType
 * @param {boolean} matchesFinePointer     matchMedia('(hover:hover) and (pointer:fine)').matches
 * @returns {'fine'|'coarse'}
 */
export function resolvePointerKind(pointerType, matchesFinePointer) {
  if (typeof pointerType === 'string' && pointerType !== '') {
    return pointerType === 'mouse' ? 'fine' : 'coarse';
  }
  return matchesFinePointer ? 'fine' : 'coarse';
}

// ── hit-box inflation ─────────────────────────────────────────────────────

/**
 * Grow a hit target toward the touch minimum WITHOUT overlapping its neighbour.
 *
 * The canvas spacing badges are the reason this is clamped rather than a flat
 * 44px: a badge's height equals the real spacing it represents, so a 6px
 * paddingTop sitting directly above a 6px marginTop would get two overlapping
 * 44px inflaters that steal each other's taps — strictly worse than the small
 * targets they replaced. Where the gap is genuinely too small the target stays
 * small and the value is edited from the drawer instead.
 *
 * @param {number} sizePx          current hit size
 * @param {number} [minPx=44]      touch minimum
 * @param {number} [availableGapPx] distance to the nearest neighbouring target
 * @returns {number} never smaller than sizePx
 */
export function inflateHitBox(sizePx, minPx = 44, availableGapPx = Infinity) {
  if (!Number.isFinite(sizePx) || sizePx < 0) return 0;
  const gap = Number.isFinite(availableGapPx) ? Math.max(0, availableGapPx) : Infinity;
  return Math.max(sizePx, Math.min(minPx, gap));
}

// ── edge autoscroll ───────────────────────────────────────────────────────

/** Largest single-tick scroll step, in visual px. */
export const AUTOSCROLL_MAX_STEP_PX = 18;
/** Edge band is 12% of the viewport, clamped to this range. */
export const AUTOSCROLL_EDGE_MIN_PX = 40;
export const AUTOSCROLL_EDGE_MAX_PX = 72;

/**
 * Scroll delta for a pointer near the edge of a scrollable viewport.
 *
 * Extracted verbatim from `_autoScrollReorder` in `reorder-preview.js` so the
 * sidebar drag (which has none today) shares one definition rather than growing
 * a second, subtly different one.
 *
 * `scale` divides the result: the iframe applies `documentElement.style.zoom`,
 * so a visual delta must be converted back to layout px. Callers with no zoom
 * pass 1.
 *
 * @returns {number} scroll delta; 0 when the pointer is outside both edge bands
 */
export function computeAutoScrollDelta(clientY, viewportTop, viewportBottom, scale = 1) {
  const height = viewportBottom - viewportTop;
  if (!Number.isFinite(height) || height <= 0) return 0;

  const edge = Math.min(AUTOSCROLL_EDGE_MAX_PX, Math.max(AUTOSCROLL_EDGE_MIN_PX, height * 0.12));

  let visualDelta = 0;
  if (clientY < viewportTop + edge) {
    visualDelta = -Math.ceil(((viewportTop + edge - clientY) / edge) * AUTOSCROLL_MAX_STEP_PX);
  } else if (clientY > viewportBottom - edge) {
    visualDelta = Math.ceil(((clientY - (viewportBottom - edge)) / edge) * AUTOSCROLL_MAX_STEP_PX);
  }
  if (!visualDelta) return 0;

  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return visualDelta / safeScale;
}

// ── sheet release (Mobile P12) ────────────────────────────────────────────

/**
 * How far ahead a release is projected, in ms of travel at the release
 * velocity. 120ms is short enough that a slow drag settles where the finger
 * left it, and long enough that a deliberate flick carries past a detent the
 * finger never physically reached — which is the whole reason a sheet feels
 * like a sheet rather than like a scrollbar.
 */
export const SHEET_PROJECTION_MS = 120;

/** Extra travel past the lowest detent before a release becomes a dismissal. */
export const SHEET_DISMISS_SLACK_PX = 24;

/**
 * Where a released sheet is heading, in px of downward offset from fully open.
 *
 * Separated from the snap decision because the two fail differently: a wrong
 * projection makes a flick feel sluggish, a wrong snap sends the sheet to the
 * wrong detent. Testing them apart says which one broke.
 *
 * @param {number} offsetPx   — current downward offset (0 = fully open)
 * @param {number} velocityPxPerMs — signed; positive is downward
 * @returns {number} projected offset, never negative
 */
export function projectSheetRelease(offsetPx, velocityPxPerMs, projectionMs = SHEET_PROJECTION_MS) {
  const offset = Number.isFinite(offsetPx) ? offsetPx : 0;
  const velocity = Number.isFinite(velocityPxPerMs) ? velocityPxPerMs : 0;
  return Math.max(0, offset + velocity * projectionMs);
}

/**
 * Resolve a released sheet to a detent, or to dismissal.
 *
 * Detents are downward offsets from fully open, ascending: `[0]` for a plain
 * modal sheet (open or gone), `[0, 180, 420]` for a detented inspector. The
 * nearest one to the projected offset wins; past the last one plus the slack,
 * the sheet is being thrown away rather than resized.
 *
 * @param {number} projectedPx — from projectSheetRelease()
 * @param {number[]} detents   — ascending offsets from open
 * @returns {{dismiss: boolean, detent: number, index: number}}
 */
export function resolveSheetSnap(projectedPx, detents = [0], slackPx = SHEET_DISMISS_SLACK_PX) {
  const stops = (Array.isArray(detents) ? detents : [0])
    .filter((d) => Number.isFinite(d))
    .sort((a, b) => a - b);
  if (!stops.length) stops.push(0);

  const projected = Number.isFinite(projectedPx) ? projectedPx : 0;
  const lowest = stops[stops.length - 1];
  if (projected > lowest + slackPx)
    return { dismiss: true, detent: lowest, index: stops.length - 1 };

  let index = 0;
  let best = Infinity;
  for (let i = 0; i < stops.length; i += 1) {
    // Strictly-less-than, so a projection exactly between two detents settles
    // on the SHALLOWER one. A sheet that resolves ambiguity by opening further
    // is recoverable with one more flick; one that closes is not.
    const distance = Math.abs(projected - stops[i]);
    if (distance < best) {
      best = distance;
      index = i;
    }
  }
  return { dismiss: false, detent: stops[index], index };
}
